import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate, useParams } from "react-router-dom";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import sanitizeHtml from "sanitize-html";
import "./NewsDetails.css";

function workflowKey(action) {
  return `news-workflow-ui:${action}:${Date.now()}:${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function NewsDetails() {
  const [newsDetails, setNewsDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const { newsId } = useParams();
  const navigate = useNavigate();

  const getNews = async () => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getNewsDetails(
        { newsId },
        getAuthConfig(),
        navigate,
      );
      setNewsDetails(response?.data || {});
    } catch (error) {
      console.error("Error fetching news details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNews();
  }, [newsId]);

  const workflow = newsDetails?.workflow || {};
  const revision = Number(workflow.revision || newsDetails.revision);

  const baseCommand = (action) => ({
    newsId,
    expectedRevision: revision,
    idempotencyKey: workflowKey(action),
  });

  const runAction = async (action) => {
    if (!revision) return;
    setWorking(true);
    let response;
    if (action === "submit") {
      response = await allAPiServicesCall.submitNewsForReview(
        baseCommand(action),
        getAuthConfig(),
        navigate,
      );
    } else if (action === "approve") {
      response = await allAPiServicesCall.approveNewsRevision(
        { ...baseCommand(action), ...(comment.trim() ? { comment: comment.trim() } : {}) },
        getAuthConfig(),
        navigate,
      );
    } else if (action === "corrections") {
      if (!comment.trim()) {
        setWorking(false);
        return;
      }
      response = await allAPiServicesCall.requestNewsCorrections(
        { ...baseCommand(action), reason: comment.trim() },
        getAuthConfig(),
        navigate,
      );
    } else if (action === "schedule") {
      if (!scheduledAt) {
        setWorking(false);
        return;
      }
      response = await allAPiServicesCall.scheduleNewsRevision(
        {
          ...baseCommand(action),
          scheduledPublishDate: new Date(scheduledAt).toISOString(),
        },
        getAuthConfig(),
        navigate,
      );
    } else if (action === "publish") {
      response = await allAPiServicesCall.publishNewsRevision(
        baseCommand(action),
        getAuthConfig(),
        navigate,
      );
    } else if (action === "revalidate") {
      response = await allAPiServicesCall.revalidateNewsRevision(
        baseCommand(action),
        getAuthConfig(),
        navigate,
      );
    }
    if (response) {
      setDialog(null);
      setComment("");
      setScheduledAt("");
      await getNews();
    }
    setWorking(false);
  };

  const checks = workflow.checks || [];
  const blockers = workflow.blockers || [];

  return (
    <CommonLayout>
      <div className="news-details-wrapper container">
        {loading ? (
          <div className="news-workflow-loading">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Card className="news-workflow-panel mt-4">
              <Card.Body>
                <div className="news-workflow-heading">
                  <div>
                    <p className="news-workflow-kicker">Editorial governance</p>
                    <h2>Review and approval</h2>
                  </div>
                  <div className="news-workflow-status">
                    <Badge bg={
                      workflow.status === "published"
                        ? "success"
                        : workflow.status === "in_review"
                          ? "primary"
                          : workflow.status === "approved"
                            ? "info"
                            : "secondary"
                    }>
                      {String(workflow.status || "unknown").replaceAll("_", " ")}
                    </Badge>
                    <span>Revision {workflow.revision || "—"}</span>
                  </div>
                </div>

                {blockers.length > 0 && (
                  <Alert variant="danger">
                    <strong>Publication blockers:</strong>{" "}
                    {blockers.join(", ")}
                  </Alert>
                )}

                {checks.length > 0 && (
                  <div className="news-workflow-checks">
                    {checks.map((check) => (
                      <div className="news-workflow-check" key={check.code}>
                        <Badge bg={
                          check.status === "PASS"
                            ? "success"
                            : check.status === "WARN"
                              ? "warning"
                              : "danger"
                        }>
                          {check.status}
                        </Badge>
                        <span>{check.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="news-workflow-actions">
                  {workflow.canEdit &&
                    blockers.includes("editorial_evidence_required") && (
                      <Button
                        variant="outline-primary"
                        disabled={working}
                        onClick={() => runAction("revalidate")}
                      >
                        Revalidate evidence
                      </Button>
                    )}
                  {workflow.canSubmit && (
                    <Button
                      disabled={working}
                      onClick={() => runAction("submit")}
                    >
                      Submit for review
                    </Button>
                  )}
                  {workflow.canApprove && (
                    <Button
                      variant="success"
                      disabled={working}
                      onClick={() => setDialog("approve")}
                    >
                      Approve this revision
                    </Button>
                  )}
                  {workflow.canRequestCorrections && (
                    <Button
                      variant="outline-warning"
                      disabled={working}
                      onClick={() => setDialog("corrections")}
                    >
                      Request corrections
                    </Button>
                  )}
                  {workflow.canSchedule && (
                    <Button
                      variant="outline-primary"
                      disabled={working}
                      onClick={() => setDialog("schedule")}
                    >
                      Schedule
                    </Button>
                  )}
                  {workflow.canPublish && (
                    <Button
                      variant="danger"
                      disabled={working}
                      onClick={() => setDialog("publish")}
                    >
                      Publish approved revision
                    </Button>
                  )}
                </div>
                {!Object.keys(workflow).length && (
                  <Alert variant="warning" className="mb-0">
                    This article does not yet expose the governed editorial
                    workflow. It cannot be approved from this screen.
                  </Alert>
                )}
              </Card.Body>
            </Card>

            <Card className="news-card mt-4">
              <Card.Img
                variant="top"
                src={
                  newsDetails.coverImg ||
                  "/assets/musical-stave-with-hearts-plain-background.jpg"
                }
                className="news-card-img text-center ms-2 rounded-3 mt-1"
              />
              <span>
                <i>Photo Credits: {newsDetails.coverImgCreds}</i>
              </span>
              <Card.Body className="news-card-body">
                <Card.Title className="news-title">
                  {newsDetails.title}
                </Card.Title>
                <Card.Text
                  className="news-description"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(newsDetails.description),
                  }}
                />
                <p className="news-author">
                  <strong>Author:</strong> {newsDetails.author}
                </p>
                <p className="news-publish-date">
                  <strong>Publication date:</strong> {newsDetails.publishDate}
                </p>
              </Card.Body>
            </Card>
          </>
        )}
      </div>

      <Modal show={Boolean(dialog)} onHide={() => setDialog(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {dialog === "approve" && "Approve immutable revision"}
            {dialog === "corrections" && "Request corrections"}
            {dialog === "schedule" && "Schedule approved revision"}
            {dialog === "publish" && "Confirm publication"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dialog === "approve" && (
            <>
              <Alert variant="info">
                You are approving revision {revision}. Any later edit will
                require a new review.
              </Alert>
              <Form.Label>Reviewer comment (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </>
          )}
          {dialog === "corrections" && (
            <>
              <Form.Label>Required corrections</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </>
          )}
          {dialog === "schedule" && (
            <>
              <Form.Label>Publication date and time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={scheduledAt}
                onChange={(event) => setScheduledAt(event.target.value)}
              />
            </>
          )}
          {dialog === "publish" && (
            <Alert variant="danger" className="mb-0">
              This publishes only the exact approved revision. Confirm only
              after reviewing the article and its evidence.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDialog(null)}>
            Cancel
          </Button>
          <Button
            variant={dialog === "publish" ? "danger" : "primary"}
            disabled={
              working ||
              (dialog === "corrections" && !comment.trim()) ||
              (dialog === "schedule" && !scheduledAt)
            }
            onClick={() => runAction(dialog)}
          >
            {working ? "Working..." : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>
    </CommonLayout>
  );
}

export default NewsDetails;
